import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { TokenService } from 'src/app/core/services/token/token.service';
import { environment } from 'src/environments/environment';

export interface ChatMessage {
  id: string;
  text: string;
  fromUser: boolean; // true = utente, false = veterinario
  timestamp: string; // ISO string dal backend
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly base = environment.apiUrl + environment.apiVersion + '/chat-veterinaria/messages';
  private readonly LAST_SEEN_KEY_PREFIX = 'chat_last_seen_message_id_';

  /** numero di messaggi non letti (solo dal vet) — alimentato dal polling globale */
  unreadCount$ = new BehaviorSubject<number>(0);

  /** emette quando arriva un nuovo messaggio dal vet — usato per banner toast */
  messageReceived$ = new Subject<boolean>();

  private bgPollingInterval: any = null;

  constructor(private tokenService: TokenService) {}

  async getMessages(): Promise<ChatMessage[]> {
    const token = await firstValueFrom(this.tokenService.getToken());
    if (!token) return [];

    try {
      const res = await fetch(this.base + '/read', {
        headers: { Authorization: token.toString() },
      });
      const data = await res.json();
      const messages: ChatMessage[] = Array.isArray(data.body) ? data.body : [];
      const idAccount = token.payload?.idAccount ?? null;
      this.updateUnreadCount(messages, idAccount);
      return messages;
    } catch (e) {
      console.warn('ChatService: errore caricamento messaggi', e);
      return [];
    }
  }

  async sendMessage(message: string): Promise<boolean> {
    const token = await firstValueFrom(this.tokenService.getToken());
    if (!token) return false;

    try {
      const res = await fetch(this.base + '/send', {
        method: 'POST',
        headers: {
          Authorization: token.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });
      return res.ok;
    } catch (e) {
      console.warn('ChatService: errore invio messaggio', e);
      return false;
    }
  }

  /** sincronizza i dati utente (animali, abbonamenti, telefono, CF) verso Freshchat — chiamato all'apertura chat */
  async syncUser(): Promise<boolean> {
    const token = await firstValueFrom(this.tokenService.getToken());
    if (!token) return false;

    try {
      const res = await fetch(this.base + '/sync-user', {
        method: 'POST',
        headers: { Authorization: token.toString() },
      });
      return res.ok;
    } catch (e) {
      console.warn('ChatService: errore sync-user', e);
      return false;
    }
  }

  async deleteMessage(messageId: string): Promise<boolean> {
    const token = await firstValueFrom(this.tokenService.getToken());
    if (!token) return false;

    try {
      const res = await fetch(this.base + '/delete', {
        method: 'POST',
        headers: {
          Authorization: token.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messageId }),
      });
      return res.ok;
    } catch (e) {
      console.warn('ChatService: errore eliminazione messaggio', e);
      return false;
    }
  }

  /** marca tutti i messaggi come letti — da chiamare quando l'utente apre la pagina chat */
  async markAllAsRead(messages?: ChatMessage[]): Promise<void> {
    if (messages && messages.length > 0) {
      const token = await firstValueFrom(this.tokenService.getToken());
      const idAccount = token?.payload?.idAccount ?? null;
      const lastId = messages[messages.length - 1].id;
      localStorage.setItem(this.lastSeenKey(idAccount), lastId);
    }
    this.unreadCount$.next(0);
  }

  // chiave per-utente in localStorage; al logout/login resta dello stesso utente
  private lastSeenKey(idAccount: number | string | null): string {
    return this.LAST_SEEN_KEY_PREFIX + (idAccount ?? 'anon');
  }

  /** avvia polling in background per aggiornare l'unread count (es. ogni 30s dalla home) */
  startBackgroundPolling(intervalMs: number = 30000): void {
    this.stopBackgroundPolling();
    this.getMessages(); // primo fetch immediato
    this.bgPollingInterval = setInterval(() => this.getMessages(), intervalMs);
  }

  stopBackgroundPolling(): void {
    if (this.bgPollingInterval) {
      clearInterval(this.bgPollingInterval);
      this.bgPollingInterval = null;
    }
  }

  /** reset stato (es. al logout): NON cancella il last_seen perché è per-utente */
  reset(): void {
    this.stopBackgroundPolling();
    this.unreadCount$.next(0);
  }

  // calcola quanti messaggi vet sono arrivati dopo l'ultimo "seen"
  private updateUnreadCount(messages: ChatMessage[], idAccount: number | string | null): void {
    if (messages.length === 0) {
      this.unreadCount$.next(0);
      return;
    }

    const lastSeenId = localStorage.getItem(this.lastSeenKey(idAccount));

    // se non c'è un last seen, considero non letti solo i messaggi vet (gli utente li ha scritti lui stesso)
    if (!lastSeenId) {
      const unread = messages.filter(m => !m.fromUser).length;
      this.emitUnread(unread);
      return;
    }

    // trovo l'indice del last seen e conto solo i messaggi vet successivi
    const idx = messages.findIndex(m => m.id === lastSeenId);
    if (idx === -1) {
      // last seen non trovato (potrebbe essere stato eliminato): considero non letti tutti i vet
      const unread = messages.filter(m => !m.fromUser).length;
      this.emitUnread(unread);
      return;
    }

    const after = messages.slice(idx + 1);
    const unread = after.filter(m => !m.fromUser).length;
    this.emitUnread(unread);
  }

  private emitUnread(count: number): void {
    const previous = this.unreadCount$.getValue();
    this.unreadCount$.next(count);
    // se il count è aumentato → nuovo messaggio dal vet, emetto evento per il banner toast
    if (count > previous) {
      this.messageReceived$.next(true);
    }
  }
}
